import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MarkdownRenderer.css';

const MarkdownRenderer = ({ content, className = '' }) => {
  // 解析图片样式参数的函数
  const parseImageStyles = (alt) => {
    if (!alt) return { alt: '', styles: {}, classes: [] };
    
    try {
      // 匹配样式参数：![alt text|width:300px|height:200px|border-radius:10px]
      const styleMatch = alt.match(/^(.+?)\|(.+)$/);
      if (!styleMatch) return { alt, styles: {}, classes: [] };
      
      const [, altText, styleString] = styleMatch;
      const styles = {};
      const classes = [];
      
      // 解析样式参数
      const styleParams = styleString.split('|');
      styleParams.forEach(param => {
        if (!param || !param.trim()) return;
        
        const paramParts = param.split(':');
        const key = paramParts[0] ? paramParts[0].trim().toLowerCase() : '';
        const value = paramParts.length > 1 ? paramParts.slice(1).join(':').trim() : '';
        
        if (!key) return;
        
        switch (key) {
          case 'width':
          case 'w':
            if (value) styles.width = value;
            break;
          case 'height':
          case 'h':
            if (value) styles.height = value;
            break;
          case 'border-radius':
          case 'radius':
          case 'r':
            if (value) styles.borderRadius = value;
            break;
          case 'size':
          case 's':
            // 预设尺寸
            if (value === 'small' || value === 's') {
              styles.width = '200px';
              classes.push('image-small');
            } else if (value === 'medium' || value === 'm') {
              styles.width = '400px';
              classes.push('image-medium');
            } else if (value === 'large' || value === 'l') {
              styles.width = '600px';
              classes.push('image-large');
            }
            break;
          case 'round':
            // 圆形图片
            if (!value || value === 'true' || value === '1') {
              styles.borderRadius = '50%';
              classes.push('image-round');
            }
            break;
          case 'shadow':
            // 阴影效果
            if (!value || value === 'true' || value === '1') {
              classes.push('image-shadow');
            }
            break;
          default:
            // 其他CSS属性直接应用
            if (value) styles[key] = value;
        }
      });
      
      return { alt: altText.trim(), styles, classes };
    } catch (error) {
      console.warn('Error parsing image styles:', error);
      return { alt, styles: {}, classes: [] };
    }
  };

  // 确保content是字符串
  const safeContent = content || '';

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={tomorrow}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          table({ children }) {
            return (
              <div className="table-wrapper">
                <table>{children}</table>
              </div>
            );
          },
          blockquote({ children }) {
            return <blockquote className="markdown-blockquote">{children}</blockquote>;
          },
          h1({ children }) {
            return <h1 className="markdown-h1">{children}</h1>;
          },
          h2({ children }) {
            return <h2 className="markdown-h2">{children}</h2>;
          },
          h3({ children }) {
            return <h3 className="markdown-h3">{children}</h3>;
          },
          a({ href, children }) {
            return (
              <a 
                href={href} 
                target="_blank" 
                rel="noopener noreferrer"
                className="markdown-link"
              >
                {children}
              </a>
            );
          },
          img({ src, alt }) {
            try {
              const { alt: parsedAlt, styles, classes } = parseImageStyles(alt);
              const imageClasses = ['markdown-image', ...(classes || [])].join(' ');
              
              return (
                <div className="markdown-image-wrapper">
                  <img 
                    src={src} 
                    alt={parsedAlt} 
                    className={imageClasses}
                    style={styles || {}}
                  />
                  {parsedAlt && <p className="markdown-image-caption">{parsedAlt}</p>}
                </div>
              );
            } catch (error) {
              console.warn('Error rendering image:', error);
              return (
                <div className="markdown-image-wrapper">
                  <img 
                    src={src} 
                    alt={alt} 
                    className="markdown-image"
                  />
                  {alt && <p className="markdown-image-caption">{alt}</p>}
                </div>
              );
            }
          }
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;